import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { formatDateTime, formatMoney, isRetryableApiError, translateApiError } from '@barahpay/shared';
import type { CheckoutEmbeddedForm } from '@barahpay/shared';
import { checkoutApi } from '../api';
import { CheckoutCard } from '../components/CheckoutCard';
import { PaymentMethodIcon } from '../components/PaymentMethodIcon';
import { PaymentProgress } from '../components/PaymentProgress';
import { ProviderWidgetMount } from '../components/ProviderWidgetMount';

/**
 * The hosted payment page (spec section 18). It shows the amount, the merchant identity, the supported
 * methods and the security indicators, then hands the payer to the provider.
 *
 * There are two ways that hand-off happens, and the server decides which: a redirect to a
 * provider-hosted page, or the provider's own form mounted here. No card field exists in this file
 * either way — under the embedded model the fields belong to frames the provider serves
 * (spec sections 61, 97 and 153).
 */
/**
 * Only an absolute http(s) URL is a usable redirect target. A relative one would resolve against the
 * checkout host, and a "javascript:" URL assigned to window.location would run on this page.
 */
function isAbsoluteHttpUrl(value: string): boolean {
  try {
    const { protocol } = new URL(value);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

export function PaymentPage() {
  const { token = '' } = useParams();
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language.startsWith('ar');

  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  const [amountInput, setAmountInput] = useState('');

  /*
   * Set once the server answers with a provider form rather than a redirect. From that point the
   * provider owns the interaction, so the method chooser and the pay button step aside.
   */
  const [embeddedForm, setEmbeddedForm] = useState<CheckoutEmbeddedForm | null>(null);

  /*
   * The failure notice is stamped with the language it was produced in, because the server-side
   * failures on this page (a decline, an unavailable provider) arrive already localised in the
   * language the request named. A message captured in English would otherwise sit under an Arabic
   * heading after a language switch, so the render drops it rather than showing it in the wrong one.
   */
  const [failure, setFailure] = useState<{ message: string; language: string } | null>(null);
  const formError = failure && failure.language === i18n.language ? failure.message : null;

  const setFormError = (message: string | null) =>
    setFailure(message === null ? null : { message, language: i18n.language });

  /*
   * Latched once the payment has left this page, and never cleared.
   *
   * `start.isPending` alone was not enough to stop a second payment. A redirect resolves the mutation
   * *before* the browser leaves — assigning window.location.href only schedules a navigation — so for
   * the moment between the response and the unload, isPending was false again and the pay button was
   * live. A second click there is a second POST /start against a session the provider has already
   * accepted, which is one payer paying twice. A ref rather than state because the guard has to hold
   * within a single event handler, before React has re-rendered anything.
   */
  const handedOff = useRef(false);
  const [handOffPhase, setHandOffPhase] = useState<'redirecting' | 'processing' | null>(null);

  // Only a retry the payer asked for disables the button. The automatic check every 15 seconds does
  // not: tying it to isFetching made the button flash to its disabled look on every background poll.
  const [retrying, setRetrying] = useState(false);

  const session = useQuery({
    queryKey: ['checkout-session', token],
    queryFn: () => checkoutApi.getSession(token),
    enabled: token.length > 0,
    /*
     * The method list is decided when the page asks for it. If the gateway was briefly out of rotation
     * at that moment, the list was empty and stayed empty until the payer thought to reload -- which
     * most do not; they leave. While a payable page has nothing to offer it asks again on its own, so
     * the methods appear as soon as the gateway is back. It stops the moment there is something to show.
     */
    refetchInterval: (query) => {
      const current = query.state.data;
      return current?.isPayable && current.paymentMethods.length === 0 ? 15_000 : false;
    },
  });

  const start = useMutation({
    mutationFn: async () => {
      const data = session.data;
      let amount: number | undefined;

      if (data?.requiresAmountEntry) {
        const parsed = Number(amountInput);

        if (!Number.isFinite(parsed) || parsed <= 0) {
          throw new Error(t('checkout.enterAmount'));
        }

        amount = parsed;
      }

      return checkoutApi.start(token, selectedMethod ?? data?.paymentMethods[0]?.method, amount);
    },
    onSuccess: (result) => {
      // The provider accepted the session and renders its own form here, so the page stays put.
      if (result.embeddedForm) {
        setEmbeddedForm(result.embeddedForm);
        setHandOffPhase('processing');
        return;
      }

      // A redirect means the provider accepted the session; anything else is shown in place.
      if (result.redirectUrl) {
        /*
         * Checked again here, even though the server refuses an unusable one. A relative URL assigned
         * to window.location resolves against this page and lands the payer on a route the app does
         * not have, which renders as "this payment page is no longer available" — a dead end that
         * looks like an expired link rather than the misconfigured provider it is. Showing the failure
         * in place keeps the payer on a page that can explain itself and offer another attempt.
         */
        if (!isAbsoluteHttpUrl(result.redirectUrl)) {
          handedOff.current = false;
          setFormError(t('checkout.paymentFailed'));
          return;
        }

        setHandOffPhase('redirecting');
        window.location.href = result.redirectUrl;
        return;
      }

      // The session was not handed off after all, so the page becomes interactive again.
      handedOff.current = false;
      setFormError(result.errorMessage ?? t('checkout.paymentFailed'));
    },
    onError: (error) => {
      handedOff.current = false;

      /*
       * A validation failure raised in mutationFn is a plain Error carrying text that is already
       * translated; anything else came from the API and is translated from its error code, which
       * covers the network and timeout cases the server never gets to report.
       */
      setFormError(
        error instanceof Error && !('status' in error) ? error.message : translateApiError(error, t),
      );
    },
  });

  if (session.isLoading) {
    return (
      <CheckoutCard>
        <div className="bp-spinner" role="status" aria-label={t('common.loading')} />
      </CheckoutCard>
    );
  }

  if (session.isError || !session.data) {
    /*
     * A payment page that could not be fetched is not necessarily a payment page that does not exist.
     * Telling a payer on a dropped connection that their link is dead sends them to support over a
     * problem a retry would have fixed, so the two are separated and only the first offers a retry.
     */
    const unreachable = isRetryableApiError(session.error);

    return (
      <CheckoutCard>
        <div className="bp-result">
          <div className="bp-result__icon bp-result__icon--error" aria-hidden="true">
            !
          </div>
          <div className="bp-result__title">
            {unreachable ? t('checkout.connectionLost') : t('checkout.notAvailable')}
          </div>
        </div>

        {unreachable ? (
          <button type="button" className="bp-button" onClick={() => void session.refetch()}>
            {t('checkout.tryAgain')}
          </button>
        ) : null}
      </CheckoutCard>
    );
  }

  const data = session.data;
  const methods = data.paymentMethods;
  const activeMethod = selectedMethod ?? methods[0]?.method ?? null;

  /*
   * The amount the payer is about to authorise, or null while a customer-entered amount is still
   * blank. It is shown twice on purpose: in the hero panel and again on the button, so the last
   * thing the payer reads before pressing is the figure they are agreeing to.
   */
  const payableAmount =
    data.requiresAmountEntry && !(Number(amountInput) > 0) ? null : data.requiresAmountEntry ? Number(amountInput) : data.amount;
  const formattedAmount = payableAmount === null ? null : formatMoney(payableAmount, data.currency, i18n.language);

  /*
   * Once the payment is in flight the card shows only its progress. Leaving the form on screen behind
   * a disabled button invites a reload, and a reload during a redirect is how a payer ends up starting
   * a second payment for the same order. The embedded-form case is excluded: there the provider's own
   * widget is the interactive surface and must stay mounted.
   */
  if (start.isPending || handOffPhase === 'redirecting') {
    return (
      <CheckoutCard merchant={data.merchant}>
        <PaymentProgress phase={start.isPending ? 'preparing' : 'redirecting'} />
      </CheckoutCard>
    );
  }

  return (
    <CheckoutCard merchant={data.merchant}>
      {!data.isPayable ? (
        <div className="bp-notice bp-notice--warning" role="status">
          {data.status === 'Succeeded'
            ? t('checkout.paymentSuccessful')
            : data.status === 'Expired'
              ? t('checkout.paymentExpired')
              : t('checkout.notAvailable')}
        </div>
      ) : null}

      <div className="bp-amount">
        <div className="bp-amount__label">{t('checkout.amountToPay')}</div>
        <div className="bp-amount__value">{formattedAmount ?? '—'}</div>
        {data.expiresAt ? (
          <div className="bp-amount__expiry">
            <ClockIcon />
            <span>
              {t('checkout.expiresAt')} · {formatDateTime(data.expiresAt, i18n.language)}
            </span>
          </div>
        ) : null}
      </div>

      <dl className="bp-detail">
        {data.referenceId ? (
          <>
            <dt>{t('checkout.reference')}</dt>
            <dd>{data.referenceId}</dd>
          </>
        ) : null}

        {data.description ? (
          <>
            <dt>{t('common.details')}</dt>
            <dd>{data.description}</dd>
          </>
        ) : null}

        {data.customerName ? (
          <>
            <dt>{t('payments.customer')}</dt>
            <dd>{data.customerName}</dd>
          </>
        ) : null}
      </dl>

      {/*
        A refused attempt no longer closes the payment, so a payer can arrive here with one behind
        them. Without a word about it the page looks exactly as it did before they paid, which is the
        state that makes someone think their card was charged twice.
      */}
      {data.isPayable && data.previousAttemptFailed && !embeddedForm ? (
        <div className="bp-notice bp-notice--warning" role="status">
          {t('checkout.previousAttemptFailed')}
        </div>
      ) : null}

      {/* Customer-entered amount within the bounds the link defines (spec section 149). */}
      {data.requiresAmountEntry && data.isPayable ? (
        <div className="bp-field">
          <label htmlFor="bp-amount-input">{t('checkout.enterAmount')}</label>
          <input
            id="bp-amount-input"
            type="number"
            inputMode="decimal"
            min={data.minAmount ?? 1}
            max={data.maxAmount ?? undefined}
            step="0.01"
            value={amountInput}
            onChange={(event) => setAmountInput(event.target.value)}
          />
          <div className="bp-field__hint">
            {data.minAmount
              ? t('checkout.minAmount', { amount: formatMoney(data.minAmount, data.currency, i18n.language) })
              : null}
            {data.minAmount && data.maxAmount ? ' · ' : null}
            {data.maxAmount
              ? t('checkout.maxAmount', { amount: formatMoney(data.maxAmount, data.currency, i18n.language) })
              : null}
          </div>
        </div>
      ) : null}

      {/*
        No methods means the platform could not offer the payer a way to pay -- every provider is
        unhealthy, or none supports this amount. The button below is disabled in that case, and a
        disabled button with nothing beside it reads as a broken page: the payer cannot tell whether
        to wait, retry, or call the merchant. Saying so is the difference between a dead end and a
        page that explains itself.
      */}
      {/*
        The two reasons read differently on purpose. A gateway briefly out of rotation comes back on its
        own and the page is already asking again (refetchInterval above), so the payer is told to stay.
        The button is for the payer who would rather not wait for the next automatic check.
      */}
      {data.isPayable && methods.length === 0 && !embeddedForm ? (
        <>
          <div className="bp-notice bp-notice--warning" role="status">
            {data.methodsUnavailableReason === 'ProviderUnavailable'
              ? t('checkout.providerTemporarilyUnavailable')
              : t('checkout.noMethodsAvailable')}
          </div>
          <button
            type="button"
            className="bp-button bp-button--secondary bp-button--retry"
            disabled={retrying}
            onClick={() => {
              setRetrying(true);
              void session.refetch().finally(() => setRetrying(false));
            }}
          >
            {t('checkout.tryAgain')}
          </button>
        </>
      ) : null}

      {data.isPayable && methods.length > 0 && !embeddedForm ? (
        <>
          <div className="bp-section-title" id="bp-methods-label">
            {t('checkout.choosePaymentMethod')}
          </div>

          {/*
            A radio group rather than a row of toggle buttons: exactly one method is chosen at a
            time, and "checked" is the state a screen reader should announce for it.
          */}
          <div className="bp-methods" role="radiogroup" aria-labelledby="bp-methods-label">
            {methods.map((method) => {
              const checked = activeMethod === method.method;

              return (
                <button
                  key={method.method}
                  type="button"
                  role="radio"
                  className="bp-method"
                  aria-checked={checked}
                  onClick={() => setSelectedMethod(method.method)}
                >
                  <span className="bp-method__icon">
                    <PaymentMethodIcon method={method.method} />
                  </span>

                  <span className="bp-method__label">
                    <span className="bp-method__name">
                      {isArabic && method.displayNameAr ? method.displayNameAr : method.displayName}
                    </span>
                    {/*
                      Translated rather than a literal: "3-D Secure" contains a hyphen, which the bidi
                      algorithm reorders to "D Secure-3" inside an Arabic paragraph.
                    */}
                    {method.supports3dSecure ? (
                      <span className="bp-method__hint">
                        <ShieldIcon />
                        {t('checkout.threeDSecure')}
                      </span>
                    ) : null}
                  </span>

                  {/*
                    The chosen method is ticked as well as tinted. aria-checked already carries the
                    state to a screen reader; this carries it to a reader who cannot pick the tint
                    out from the row beside it.
                  */}
                  <span className="bp-method__check" aria-hidden="true">
                    <CheckIcon />
                  </span>
                </button>
              );
            })}
          </div>
        </>
      ) : null}

      {formError ? (
        <div className="bp-notice bp-notice--error" role="alert">
          {formError}
        </div>
      ) : null}

      {/*
        Once the provider's form is mounted it owns the interaction: it collects the details, runs any
        3-D Secure step-up, and submits to the result page itself. A second pay button here would give
        the payer two ways to start one payment.
      */}
      {embeddedForm ? <ProviderWidgetMount form={embeddedForm} /> : null}

      {data.isPayable && !embeddedForm ? (
        <>
          <button
            type="button"
            className="bp-button"
            disabled={start.isPending || handOffPhase !== null || methods.length === 0}
            onClick={() => {
              // The ref, not the disabled attribute, is what makes this safe: a double click can
              // deliver its second event before React has re-rendered the button as disabled.
              if (handedOff.current) {
                return;
              }

              handedOff.current = true;
              setFormError(null);
              start.mutate();
            }}
          >
            <LockIcon />
            <span>{formattedAmount ? t('checkout.payAmount', { amount: formattedAmount }) : t('checkout.payNow')}</span>
          </button>

          <div className="bp-security">
            <span className="bp-security__icon" aria-hidden="true">
              <LockIcon />
            </span>
            <span>{t('checkout.cardDetailsNotice')}</span>
          </div>
        </>
      ) : null}
    </CheckoutCard>
  );
}

/* Small inline glyphs. Drawn here rather than loaded: the page's policy allows no icon font or sprite. */

function CheckIcon() {
  return (
    <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false">
      <path
        d="M3.5 8.5l3 3 6-7"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <rect x="3" y="7" width="10" height="7.5" rx="1.8" fill="currentColor" />
      <path d="M5.25 7V5.25a2.75 2.75 0 0 1 5.5 0V7" fill="none" stroke="currentColor" strokeWidth="1.7" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true" focusable="false">
      <path
        d="M8 1.5l5 2v4.2c0 3.1-2.1 5.5-5 6.8-2.9-1.3-5-3.7-5-6.8V3.5l5-2z"
        fill="currentColor"
        opacity="0.9"
      />
      <path d="M5.6 8.1l1.7 1.7 3.2-3.5" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false">
      <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M8 4.8V8l2.2 1.4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}
